import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import { setAuthToken, setMe, setOtpVerified } from "@actions/sessionActions";
import TertiaryButton from "@components/buttons/TertiaryButton";
import PhoneNumberForm from "@components/forms/PhoneNumberForm";
import PhoneVerificationForm from "@components/forms/PhoneVerificationForm";
import HugeText from "@components/text/HugeText";
import TextWithIcon from "@components/text/TextWithIcon";
import styles from "@components/TwoFactorAuthentication.module.css";
import { sessionContext } from "@context/Contexts";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import { thirtySecondsInMs } from "@typing/Constants";
import {
  PhoneNumberFragment as PhoneNumber,
  PhoneNumberChannelEnum,
  PhoneVerificationCreateDocument,
  PhoneVerificationCreateMutation,
  PhoneVerificationCreateMutationVariables,
  PhoneVerificationVerifyMutation
} from "@typing/Generated";

type Props = {
  client?: {
    id: string;
    verificationPhoneNumber: PhoneNumber | null;
  };
  onCancel?: () => void;
  settingUp?: boolean;
};

type ClientResponse = NonNullable<NonNullable<PhoneVerificationVerifyMutation["phoneVerificationVerify"]>["client"]>;

enum Steps {
  ENTER_NUMBER = "enter_number",
  VERIFY_NUMBER = "verify_number"
}

const TwoFactorAuthentication = ({ client, onCancel, settingUp = false }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const history = useHistory();

  const { dispatch } = useContext(sessionContext);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const previouslyVerified = useMemo(
    () => !!(client?.verificationPhoneNumber && client.verificationPhoneNumber.verified),
    [client]
  );

  const [step, setStep] = useState(previouslyVerified ? Steps.VERIFY_NUMBER : Steps.ENTER_NUMBER);

  const handlePhoneNumberSuccess = () => {
    setStep(Steps.VERIFY_NUMBER);
  };

  const handleVerificationSuccess = (client: ClientResponse, jwt: string) => {
    dispatch(setMe(client));
    if (myClient && !settingUp) {
      dispatch(setAuthToken(jwt, myClient));
      dispatch(setOtpVerified(true));
      const storedPathname = sessionStorage.getItem("postLoginRedirectTo");
      sessionStorage.removeItem("postLoginRedirectTo");

      history.push(storedPathname ?? "/client");
    }
    if (onCancel) {
      onCancel();
    }
  };

  const [resendVerificationCode] = useMutationWithValidation<
    PhoneVerificationCreateMutation,
    PhoneVerificationCreateMutationVariables
  >(PhoneVerificationCreateDocument, "phoneNumber");

  useEffect(() => {
    if (client?.verificationPhoneNumber === null && !settingUp) {
      history.push("/log-in");
    }
  }, [client?.verificationPhoneNumber, history, settingUp]);

  const resendCode = useCallback(
    (channel: PhoneNumberChannelEnum) => {
      if (resendDisabled) {
        setShowLoader(true);
      } else {
        resendVerificationCode({
          channel
        }).then(() => {
          setResendDisabled(true);
          setTimeout(() => {
            setResendDisabled(false);
            setShowLoader(false);
          }, thirtySecondsInMs);
        });
      }
    },
    [resendDisabled, resendVerificationCode]
  );

  useEffect(() => {
    let ignore = false;
    if (client?.id && previouslyVerified && step === Steps.VERIFY_NUMBER) {
      if (!ignore) {
        resendVerificationCode({
          channel: PhoneNumberChannelEnum.sms
        });
      }
    }

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!client) {
    return null;
  }

  return (
    <div className={styles.container}>
      {step === Steps.ENTER_NUMBER ? (
        <div>
          <HugeText>
            <FormattedMessage id="pages.client.account.twoFactor.secureAccount" />
          </HugeText>
          <div className="ion-margin-top">
            <FormattedMessage id="pages.client.account.twoFactor.enterANumber" />
          </div>
          <PhoneNumberForm onCancel={onCancel} onSuccess={handlePhoneNumberSuccess} />
        </div>
      ) : (
        <div>
          <HugeText>
            <FormattedMessage id="pages.client.account.twoFactor.enterCode" />
          </HugeText>
          <div className="ion-margin-top">
            <FormattedMessage
              id="pages.client.account.twoFactor.inputCode"
              values={{ number: client.verificationPhoneNumber?.formattedNumber }}
            />
          </div>
          <PhoneVerificationForm client={client} onCancel={onCancel} onSuccess={handleVerificationSuccess} />
          {showLoader ? (
            <TextWithIcon
              icon="spinner"
              iconFirst
              text={intl.formatMessage({ id: "pages.client.account.twoFactor.wait" })}
            />
          ) : (
            <>
              <div className={styles.spacing}>
                <FormattedMessage id="pages.client.account.twoFactor.noCode" />
              </div>
              <div className={styles.flex}>
                <div className="ion-margin-end">
                  <TertiaryButton
                    icon="repeat"
                    onClick={() => {
                      resendCode(PhoneNumberChannelEnum.sms);
                    }}
                    textKey="pages.client.account.twoFactor.resendCode"
                  />
                </div>
                <div>
                  <TertiaryButton
                    icon="phone"
                    onClick={() => {
                      resendCode(PhoneNumberChannelEnum.call);
                    }}
                    textKey="pages.client.account.twoFactor.receiveCall"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuthentication;
